import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { View, Text, Pressable, Platform, useWindowDimensions, ActivityIndicator } from 'react-native'
import { useLocalSearchParams, useRouter, type Href } from 'expo-router'
import { Check } from 'lucide-react-native'
import { useSupabaseAuth } from '../../../src/shared/contexts/SupabaseAuthContext'
import { useAccountAdmin } from '../../../src/accounts/hooks/useAccountAdmin'
import { useB2bBillingActions } from '../../../src/accounts/hooks/useB2bBillingActions'
import { useConsumerProBillingActions } from '../../../src/accounts/hooks/useConsumerProBillingActions'
import { useApiClient } from '../../../helpers/apiClient.helper'
import { API_ENDPOINTS } from '../../../constants/api'
import { isCurrentPlan, resolveCtaState } from '../../../src/packs/helpers/pricingCtaState.helper'
import { isOrgB2bStripeSubscriptionStatusPaid } from '../../../src/packs/helpers/pricingOrgSubscription.helper'
import { invokePricingCtaAction } from '../../../src/packs/helpers/pricingCtaPress.helper'
import {
  isPricingProductionMode,
  resolvePricingPlanForEnv,
  shouldHideBusinessSelfServePricingUi,
} from '../../../src/packs/helpers/pricingProductionMode.helper'
import { LanguageContext } from '../../../src/i18n'
import {
  PRICING_PLAN_TIERS,
  isConsumerProPricingPlanTier,
} from '../../../src/packs/constants/pricingPlans.constants'
import type { BusinessSeatBucket } from '../../../src/packs/constants/pricingPlans.constants'
import { usePricingI18n } from '../../../src/packs/hooks/usePricingI18n'
import type { PricingCopyLabels } from '../../../src/packs/helpers/pricingI18n.helper'
import { ENTERPRISE_CONTACT_PATH } from '../../../src/packs/constants/enterpriseContact.constants'
import { buildPublicSignInHref } from '../../../src/signIn/helpers/postAuthRedirect.helper'
import {
  ACCOUNT_ADMIN_NAV,
  ACCOUNT_ADMIN_PUBLIC_ROUTE,
} from '../../../src/accounts/constants/accountAdmin.constants'
import { buildAccountAdminNavPath } from '../../../src/accounts/helpers/accountAdminNav.helper'
import { useStableConsumerPlanForPricing } from '../../../src/packs/hooks/useStableConsumerPlanForPricing'
import { useTheme } from '../../../src/theme/useTheme'
import { getLandingPricingPackagesStyles } from './LandingPricingPackages.styles'
import { LANDING_PHONE_MAX_WIDTH } from '../../../src/landing/constants/landingMarketingBreakpoints.constants'
import {
  LANDING_PAGE_LEGACY_SECTION_PRICING,
  LANDING_SITE_NAV_SCROLL_OFFSET_PX,
} from '../../../src/landing/constants/landingSiteNav.constants'
import { scrollWebWindowToElement } from '../../../src/landing/helpers/scrollWebMarketingSection.helper'
import type { ConsumerProBillingInterval } from '../../../src/packs/constants/consumerProBilling.constants'
import type { PricingPlanCard } from '../../../src/packs/constants/pricingPlans.constants'
import type { Theme } from '../../../src/theme/getTheme'
import { SeatBucketPicker } from '../../../src/packs/components/SeatBucketPicker/SeatBucketPicker'

const PRICING_STACKED_WIDTH = 1060
const PRICING_PHONE_WIDTH = LANDING_PHONE_MAX_WIDTH

function billingQueryFlag(billing: string | string[] | undefined): string {
  const b = billing
  const raw = typeof b === 'string' ? b : Array.isArray(b) ? b[0] : undefined
  return typeof raw === 'string' ? raw.trim().toLowerCase() : ''
}

function sectionQueryFlag(section: string | string[] | undefined): string | undefined {
  const raw = typeof section === 'string' ? section : Array.isArray(section) ? section[0] : undefined
  return typeof raw === 'string' ? raw.trim().toLowerCase() : undefined
}

export function LandingPricingPackages() {
  const theme = useTheme()
  const styles = useMemo(() => getLandingPricingPackagesStyles(theme), [theme])
  const router = useRouter()
  const params = useLocalSearchParams<{ section?: string | string[]; billing?: string | string[]; session_id?: string | string[] }>()
  const { width } = useWindowDimensions()
  const { isLoaded, isSignedIn } = useSupabaseAuth()
  const { user, loading: accountAdminLoading, refresh: refreshAccountAdmin } = useAccountAdmin()
  const apiClient = useApiClient()
  const { consumerPlanTier, consumerProBillingInterval } = useStableConsumerPlanForPricing(
    user,
    accountAdminLoading,
  )
  const orgPlanTier = user?.account?.planTier ?? null
  const orgMaxSeats = user?.account?.maxSeats ?? null
  const accountId = user?.account?.id ?? null
  const account = user?.account ?? undefined
  const isB2bOrgAdmin = user?.membership?.role === 'ADMIN'
  const stripeNegotiatedPriceId = account?.stripeNegotiatedPriceId ?? null
  const stripeNegotiatedPriceDisplay = account?.stripeNegotiatedPriceDisplay ?? null
  const orgB2bStripeSubscriptionPaid = useMemo(
    () => isOrgB2bStripeSubscriptionStatusPaid(account?.stripeSubscriptionStatus),
    [account?.stripeSubscriptionStatus],
  )
  const languageContext = useContext(LanguageContext)
  if (!languageContext) {
    throw new Error('LandingPricingPackages must be used within LanguageProvider')
  }
  const { t } = languageContext
  const { planCards, copy, seatBuckets } = usePricingI18n()
  const layoutWidth = width > 0 ? width : 390
  const isStacked = layoutWidth < PRICING_STACKED_WIDTH
  const isPhone = layoutWidth < PRICING_PHONE_WIDTH
  const isProductionMode = isPricingProductionMode()

  const billingReturnHandledRef = useRef(false)
  useEffect(() => {
    if (Platform.OS !== 'web') return
    const section = sectionQueryFlag(params.section)
    if (section !== LANDING_PAGE_LEGACY_SECTION_PRICING) {
      billingReturnHandledRef.current = false
      return
    }
    const billingFlag = billingQueryFlag(params.billing)
    if (billingFlag !== 'success' && billingFlag !== 'cancel') {
      return
    }
    if (billingReturnHandledRef.current) return
    billingReturnHandledRef.current = true
    const afterReplaceScrollToPricing = (): void => {
      window.setTimeout(() => {
        if (typeof document === 'undefined') return
        scrollWebWindowToElement(
          document.getElementById('pricing'),
          LANDING_SITE_NAV_SCROLL_OFFSET_PX,
        )
      }, 380)
    }
    if (billingFlag === 'cancel') {
      router.replace('/' as Href)
      afterReplaceScrollToPricing()
      return
    }
    if (user) {
      const sessionIdParam = params.session_id
      const sessionId =
        typeof sessionIdParam === 'string'
          ? sessionIdParam
          : Array.isArray(sessionIdParam)
            ? sessionIdParam[0]
            : undefined
      const checkoutSessionId =
        typeof sessionId === 'string' && sessionId.startsWith('cs_') ? sessionId : null

      const syncAndRefresh = async () => {
        if (checkoutSessionId) {
          try {
            await apiClient(API_ENDPOINTS.billing.consumerProSyncCheckoutSession, {
              method: 'POST',
              data: { sessionId: checkoutSessionId },
            })
          } catch {
            // If sync fails (e.g. already synced via webhook), still refresh
          }
        }
        await refreshAccountAdmin()
        router.replace('/' as Href)
        afterReplaceScrollToPricing()
      }
      void syncAndRefresh()
      return
    }
    router.replace('/' as Href)
    afterReplaceScrollToPricing()
  }, [params.section, params.billing, params.session_id, user, refreshAccountAdmin, router, apiClient])

  const pricingDeepLinkHandledRef = useRef(false)
  useEffect(() => {
    if (Platform.OS !== 'web') return
    const section = sectionQueryFlag(params.section)
    if (section !== LANDING_PAGE_LEGACY_SECTION_PRICING) {
      pricingDeepLinkHandledRef.current = false
      return
    }
    const billingFlag = billingQueryFlag(params.billing)
    if (billingFlag === 'success' || billingFlag === 'cancel') {
      return
    }
    if (pricingDeepLinkHandledRef.current) return
    pricingDeepLinkHandledRef.current = true
    router.replace('/' as Href)
    window.setTimeout(() => {
      if (typeof document === 'undefined') return
      scrollWebWindowToElement(
        document.getElementById('pricing'),
        LANDING_SITE_NAV_SCROLL_OFFSET_PX,
      )
    }, 380)
  }, [params.section, params.billing, router])

  const { startCheckout, busy: busyB2b } = useB2bBillingActions(accountId, account)
  const { startConsumerProCheckout, openConsumerProPortal, busy: busyConsumer } =
    useConsumerProBillingActions(user?.consumerBilling)

  const [selectedBucketKey, setSelectedBucketKey] = useState(seatBuckets[0].key)
  const userPickedBucketRef = useRef(false)
  const selectBucketKey = useCallback((key: string) => {
    userPickedBucketRef.current = true
    setSelectedBucketKey(key)
  }, [])
  // Default the picker to the org's current bucket once it loads, so existing
  // Business orgs see their own price ($550) — not the catalog's first tier ($450).
  useEffect(() => {
    if (userPickedBucketRef.current) return
    if (orgPlanTier !== 'BUSINESS' || typeof orgMaxSeats !== 'number') return
    const current = seatBuckets.find((b) => b.maxSeats === orgMaxSeats)
    if (current && current.key !== selectedBucketKey) {
      setSelectedBucketKey(current.key)
    }
  }, [orgPlanTier, orgMaxSeats, selectedBucketKey, seatBuckets])
  const selectedBucketMaxSeats = useMemo(
    () => seatBuckets.find((b) => b.key === selectedBucketKey)?.maxSeats ?? null,
    [selectedBucketKey, seatBuckets],
  )

  const ctaDeps = useMemo(
    () => ({
      onB2bCheckout: (tier: 'BUSINESS' | 'ENTERPRISE') => {
        const bucketKey = tier === 'BUSINESS' ? selectedBucketKey : undefined
        void startCheckout(tier, bucketKey)
      },
      onConsumerProCheckout: (billingInterval: ConsumerProBillingInterval) => {
        void startConsumerProCheckout(billingInterval)
      },
      onSignIn: () => {
        router.push(buildPublicSignInHref({ returnUrl: '/' }) as Href)
      },
      onContinueToApp: () => {
        router.push('/app/friends' as Href)
      },
      onContactSales: () => {
        router.push(ENTERPRISE_CONTACT_PATH as Href)
      },
      onCreateOrg: () => {
        const href = (
          user ? buildAccountAdminNavPath(ACCOUNT_ADMIN_NAV.START_HERE) : ACCOUNT_ADMIN_PUBLIC_ROUTE
        ) as Href
        router.push(href)
      },
      onManageConsumerSubscription: () => {
        void openConsumerProPortal()
      },
      onManageB2bBilling: () => {
        const href = (
          user ? buildAccountAdminNavPath(ACCOUNT_ADMIN_NAV.PLAN_BILLING) : ACCOUNT_ADMIN_PUBLIC_ROUTE
        ) as Href
        router.push(href)
      },
      onScheduleDowngradeToMonthly: () => {
        void openConsumerProPortal()
      },
    }),
    [router, user, startCheckout, startConsumerProCheckout, openConsumerProPortal, selectedBucketKey],
  )

  const planPressHandlers = useMemo(() => {
    const hasOrg = user?.account != null
    const signedIn = Boolean(isLoaded && isSignedIn)
    const map: Partial<Record<string, () => void>> = {}
    for (const plan of planCards) {
      const resolvedPlan = resolvePricingPlanForEnv(plan, isProductionMode)
      const bucketMaxSeats =
        plan.tier === PRICING_PLAN_TIERS.BUSINESS ? selectedBucketMaxSeats : null
      const state = resolveCtaState(
        resolvedPlan,
        orgPlanTier,
        consumerPlanTier,
        consumerProBillingInterval,
        hasOrg,
        signedIn,
        accountAdminLoading,
        busyB2b,
        busyConsumer,
        stripeNegotiatedPriceId,
        isB2bOrgAdmin,
        orgB2bStripeSubscriptionPaid,
        orgMaxSeats,
        bucketMaxSeats,
        copy,
      )
      if (state.action === 'none' || state.label.length === 0) {
        continue
      }
      map[plan.tier] = () => {
        invokePricingCtaAction(resolvedPlan, state, ctaDeps)
      }
    }
    return map
  }, [
    user?.account,
    isLoaded,
    isSignedIn,
    orgPlanTier,
    orgMaxSeats,
    consumerPlanTier,
    consumerProBillingInterval,
    accountAdminLoading,
    busyB2b,
    busyConsumer,
    stripeNegotiatedPriceId,
    isB2bOrgAdmin,
    orgB2bStripeSubscriptionPaid,
    isProductionMode,
    ctaDeps,
    selectedBucketMaxSeats,
    copy,
    planCards,
  ])

  return (
    <View style={[styles.section, isPhone && styles.sectionPhone]} nativeID="pricing">
      <View style={styles.container}>
        <View style={[styles.headerRow, isPhone && styles.headerRowPhone]}>
          <View style={styles.headerCopy}>
            <Text style={styles.eyebrow}>{t.pricing.eyebrow}</Text>
            <Text style={[styles.h2, isPhone && styles.h2Phone]}>{copy.pageTitle}</Text>
            <Text style={[styles.p, isPhone && styles.pPhone]}>{copy.pageSubtitle}</Text>
          </View>
        </View>

        <View style={isStacked ? styles.cardsStacked : styles.cardsGrid}>
          {isStacked ? (
            planCards.map((plan, index) => {
              const resolvedPlan = resolvePricingPlanForEnv(plan, isProductionMode)
              return (
                <LandingPlanCard
                  key={plan.tier}
                  theme={theme}
                  styles={styles}
                  plan={resolvedPlan}
                  isStacked={isStacked}
                  isPhone={isPhone}
                  isLastCard={index === planCards.length - 1}
                  isSignedIn={Boolean(isLoaded && isSignedIn)}
                  consumerPlanTier={consumerPlanTier}
                  consumerProBillingInterval={consumerProBillingInterval}
                  orgPlanTier={orgPlanTier}
                  hasOrg={user?.account != null}
                  isB2bOrgAdmin={isB2bOrgAdmin}
                  stripeNegotiatedPriceId={stripeNegotiatedPriceId}
                  stripeNegotiatedPriceDisplay={stripeNegotiatedPriceDisplay}
                  accountAdminLoading={accountAdminLoading}
                  busyB2b={busyB2b}
                  busyConsumer={busyConsumer}
                  orgB2bStripeSubscriptionPaid={orgB2bStripeSubscriptionPaid}
                  orgMaxSeats={plan.tier === PRICING_PLAN_TIERS.BUSINESS ? orgMaxSeats : undefined}
                  selectedBucketKey={plan.tier === PRICING_PLAN_TIERS.BUSINESS ? selectedBucketKey : undefined}
                  onSelectBucketKey={plan.tier === PRICING_PLAN_TIERS.BUSINESS ? selectBucketKey : undefined}
                  copy={copy}
                  seatBuckets={seatBuckets}
                  onPress={planPressHandlers[plan.tier]}
                />
              )
            })
          ) : (
            <>
              <View style={styles.cardsRow1}>
                {planCards.slice(0, 3).map((plan) => {
                  const resolvedPlan = resolvePricingPlanForEnv(plan, isProductionMode)
                  return (
                    <LandingPlanCard
                      key={plan.tier}
                      theme={theme}
                      styles={styles}
                      plan={resolvedPlan}
                      isStacked={false}
                      isPhone={isPhone}
                      isLastCard={false}
                      isSignedIn={Boolean(isLoaded && isSignedIn)}
                      consumerPlanTier={consumerPlanTier}
                      consumerProBillingInterval={consumerProBillingInterval}
                      orgPlanTier={orgPlanTier}
                      hasOrg={user?.account != null}
                      isB2bOrgAdmin={isB2bOrgAdmin}
                      stripeNegotiatedPriceId={stripeNegotiatedPriceId}
                      stripeNegotiatedPriceDisplay={stripeNegotiatedPriceDisplay}
                      accountAdminLoading={accountAdminLoading}
                      busyB2b={busyB2b}
                      busyConsumer={busyConsumer}
                      orgB2bStripeSubscriptionPaid={orgB2bStripeSubscriptionPaid}
                      copy={copy}
                      seatBuckets={seatBuckets}
                      onPress={planPressHandlers[plan.tier]}
                    />
                  )
                })}
              </View>
              <View style={styles.cardsRow2}>
                {planCards.slice(3).map((plan) => {
                  const resolvedPlan = resolvePricingPlanForEnv(plan, isProductionMode)
                  return (
                    <LandingPlanCard
                      key={plan.tier}
                      theme={theme}
                      styles={styles}
                      plan={resolvedPlan}
                      isStacked={false}
                      isPhone={isPhone}
                      isLastCard={false}
                      isSignedIn={Boolean(isLoaded && isSignedIn)}
                      consumerPlanTier={consumerPlanTier}
                      consumerProBillingInterval={consumerProBillingInterval}
                      orgPlanTier={orgPlanTier}
                      hasOrg={user?.account != null}
                      isB2bOrgAdmin={isB2bOrgAdmin}
                      stripeNegotiatedPriceId={stripeNegotiatedPriceId}
                      stripeNegotiatedPriceDisplay={stripeNegotiatedPriceDisplay}
                      accountAdminLoading={accountAdminLoading}
                      busyB2b={busyB2b}
                      busyConsumer={busyConsumer}
                      orgB2bStripeSubscriptionPaid={orgB2bStripeSubscriptionPaid}
                      orgMaxSeats={plan.tier === PRICING_PLAN_TIERS.BUSINESS ? orgMaxSeats : undefined}
                      selectedBucketKey={plan.tier === PRICING_PLAN_TIERS.BUSINESS ? selectedBucketKey : undefined}
                      onSelectBucketKey={plan.tier === PRICING_PLAN_TIERS.BUSINESS ? selectBucketKey : undefined}
                      copy={copy}
                      seatBuckets={seatBuckets}
                      onPress={planPressHandlers[plan.tier]}
                    />
                  )
                })}
              </View>
            </>
          )}
        </View>
        <Text style={[styles.footnote, isPhone && styles.footnotePhone]}>
          {copy.footnote}
        </Text>
      </View>
    </View>
  )
}

interface LandingPlanCardProps {
  theme: Theme
  styles: ReturnType<typeof getLandingPricingPackagesStyles>
  plan: PricingPlanCard
  isStacked: boolean
  isPhone: boolean
  isLastCard: boolean
  isSignedIn: boolean
  consumerPlanTier: 'FREE' | 'PRO'
  consumerProBillingInterval: ConsumerProBillingInterval | null
  orgPlanTier: string | null
  hasOrg: boolean
  isB2bOrgAdmin: boolean
  stripeNegotiatedPriceId: string | null
  stripeNegotiatedPriceDisplay: { main: string; sub: string } | null
  accountAdminLoading: boolean
  busyB2b: boolean
  busyConsumer: boolean
  orgB2bStripeSubscriptionPaid: boolean
  /** Current org maxSeats — only relevant for the BUSINESS card. */
  orgMaxSeats?: number | null
  /** Selected bucket key — only relevant for the BUSINESS card. */
  selectedBucketKey?: string
  /** Callback when a bucket pill is tapped — only relevant for the BUSINESS card. */
  onSelectBucketKey?: (key: string) => void
  copy: PricingCopyLabels
  seatBuckets: readonly BusinessSeatBucket[]
  onPress?: () => void
}

function LandingPlanCard({
  theme,
  styles,
  plan,
  isStacked,
  isPhone,
  isLastCard,
  isSignedIn,
  consumerPlanTier,
  consumerProBillingInterval,
  orgPlanTier,
  hasOrg,
  isB2bOrgAdmin,
  stripeNegotiatedPriceId,
  stripeNegotiatedPriceDisplay,
  accountAdminLoading,
  busyB2b,
  busyConsumer,
  orgB2bStripeSubscriptionPaid,
  orgMaxSeats,
  selectedBucketKey,
  onSelectBucketKey,
  copy,
  seatBuckets,
  onPress,
}: LandingPlanCardProps) {
  const Icon = plan.icon
  const isProConsumerVisual = isConsumerProPricingPlanTier(plan.tier)
  const isBusinessCard = plan.tier === PRICING_PLAN_TIERS.BUSINESS
  const hideBusinessSelfServePricing = shouldHideBusinessSelfServePricingUi(plan, orgPlanTier, hasOrg)

  const selectedBucket = isBusinessCard && selectedBucketKey
    ? (seatBuckets.find((b) => b.key === selectedBucketKey) ?? null)
    : null
  const selectedBucketMaxSeats = selectedBucket?.maxSeats ?? null

  const visibleBuckets = useMemo(() => {
    if (!isBusinessCard) return seatBuckets
    if (orgPlanTier === 'BUSINESS' && typeof orgMaxSeats === 'number') {
      return seatBuckets.filter((b) => b.maxSeats >= orgMaxSeats)
    }
    return seatBuckets
  }, [isBusinessCard, orgPlanTier, orgMaxSeats, seatBuckets])

  // For Business: "Your plan" only when the selected bucket matches the org's actual bucket
  // (so picking a higher tier in the dropdown removes the badge).
  const isPlanCurrent = isBusinessCard
    ? orgPlanTier === 'BUSINESS'
      && typeof orgMaxSeats === 'number'
      && selectedBucketMaxSeats === orgMaxSeats
    : isCurrentPlan(plan.tier, orgPlanTier, consumerPlanTier, consumerProBillingInterval)

  const ctaState = resolveCtaState(
    plan,
    orgPlanTier,
    consumerPlanTier,
    consumerProBillingInterval,
    hasOrg,
    isSignedIn,
    accountAdminLoading,
    busyB2b,
    busyConsumer,
    stripeNegotiatedPriceId,
    isB2bOrgAdmin,
    orgB2bStripeSubscriptionPaid,
    orgMaxSeats,
    selectedBucketMaxSeats,
    copy,
  )
  const { label: ctaLabel, action: ctaAction, outline: isOutline, disabled: ctaDisabled } = ctaState
  const showCtaPressable = ctaAction !== 'none' && ctaLabel.length > 0
  const showCardYourPlanBorder = isPlanCurrent

  const isBusy =
    ctaAction === 'consumer-checkout' || ctaAction === 'manage-consumer-billing'
      ? busyConsumer
      : ctaAction === 'checkout'
        ? busyB2b
        : false

  return (
    <View
      style={[
        styles.card,
        isStacked && styles.cardStacked,
        isStacked && isLastCard && styles.cardStackedLast,
        isPhone && styles.cardPhone,
        showCardYourPlanBorder && styles.cardYourPlan,
      ]}
    >
      <View style={styles.iconBadgeRow}>
        <View
          style={[
            styles.iconWrap,
            isPhone && styles.iconWrapPhone,
            isProConsumerVisual && styles.iconWrapFeatured,
          ]}
        >
          <Icon size={isPhone ? 20 : 24} color={isProConsumerVisual ? theme.white : theme.primary} />
        </View>
        {plan.comingSoon ? (
          <View style={styles.badgeSlot}>
            <View style={styles.badge}>
              <Text style={styles.badgeText}>{copy.comingSoonBadge}</Text>
            </View>
          </View>
        ) : null}
        {plan.mostPopular === true && !plan.comingSoon && !isPlanCurrent ? (
          <View style={styles.badgeSlot}>
            <View style={styles.badge}>
              <Text style={styles.badgeText}>{copy.mostPopularBadge}</Text>
            </View>
          </View>
        ) : null}
      </View>
      <Text style={[styles.cardTitle, isPhone && styles.cardTitlePhone]}>{plan.name}</Text>
      <Text style={styles.cardPrice}>
        {hideBusinessSelfServePricing
          ? copy.comingSoonInProduction
          : selectedBucket
            ? selectedBucket.priceLabel
            : plan.tier === PRICING_PLAN_TIERS.ENTERPRISE && stripeNegotiatedPriceDisplay
              ? `${stripeNegotiatedPriceDisplay.main} / ${stripeNegotiatedPriceDisplay.sub}`
              : `${plan.priceLabel}${plan.priceSubLabel ? ` / ${plan.priceSubLabel}` : ''}`}
      </Text>
      <Text style={[styles.cardDescription, isPhone && styles.cardDescriptionPhone]}>{plan.tagline}</Text>
      {isPlanCurrent ? (
        <View style={styles.badgeYourPlan}>
          <Text style={styles.badgeYourPlanText}>{copy.currentPlanBadge}</Text>
        </View>
      ) : null}
      <View style={[styles.bullets, isStacked && styles.bulletsStacked]}>
        {plan.features.filter((f) => f.included).map((f) => (
          <View key={f.label} style={styles.bulletRow}>
            <View style={styles.checkWrap}>
              <Check size={16} color={theme.success} />
            </View>
            <Text style={[styles.bulletText, isPhone && styles.bulletTextPhone]}>{f.label}</Text>
          </View>
        ))}
      </View>
      {isBusinessCard
      && !hideBusinessSelfServePricing
      && onSelectBucketKey != null
      && selectedBucketKey != null ? (
        <SeatBucketPicker
          buckets={visibleBuckets}
          selectedKey={selectedBucketKey}
          onSelect={onSelectBucketKey}
        />
      ) : null}
      {isBusinessCard && !hideBusinessSelfServePricing ? (
        <Text style={[styles.orgPlanDisclosure, isPhone && styles.orgPlanDisclosurePhone]}>
          {copy.orgPlanDisclosure}
        </Text>
      ) : null}
      {showCtaPressable ? (
        <Pressable
          accessibilityRole="button"
          disabled={ctaDisabled || onPress == null}
          onPress={onPress}
          style={({ hovered }) => [
            styles.cardCta,
            { flexDirection: 'row' },
            isOutline && styles.cardCtaCompany,
            hovered && styles.cardCtaHovered,
            (ctaDisabled || onPress == null) && styles.cardCtaDisabled,
          ]}
        >
          {isBusy ? (
            <ActivityIndicator
              size="small"
              color={isOutline ? theme.primary : '#FFFFFF'}
              style={{ marginRight: 6 }}
            />
          ) : null}
          <Text style={[styles.cardCtaText, isOutline && styles.cardCtaCompanyText]}>
            {ctaLabel}
          </Text>
        </Pressable>
      ) : null}
    </View>
  )
}
